
import { ChitPool, DashboardData, Loan } from "@/lib/types";

export const calculateUniqueCustomers = (
  hasChitPoolData: boolean, 
  hasLoanData: boolean, 
  chitPools?: any[], 
  loans?: any[]
): number => {
  const uniqueCustomers = new Set();
  
  // Add members from chit pools
  if (hasChitPoolData && chitPools) {
    chitPools.forEach(pool => {
      // Check if the pool has a members property and it's an array
      const poolMembers = Array.isArray(pool.members) ? pool.members : [];
      poolMembers.forEach(member => {
        if (member && member.whatsapp_number) {
          uniqueCustomers.add(member.whatsapp_number);
        } else if (member && member.whatsappNumber) {
          uniqueCustomers.add(member.whatsappNumber);
        }
      });
    });
  }
  
  // Add loan customers
  if (hasLoanData && loans) {
    loans.forEach(loan => {
      // Use whatsapp_number property which should be consistent with our type definitions
      if (loan.whatsapp_number) {
        uniqueCustomers.add(loan.whatsapp_number);
      } else if (loan.whatsappNumber) { 
        uniqueCustomers.add(loan.whatsappNumber);
      } else if (loan.customer_name) {
        // As a fallback, we can use the customer name if the number isn't available
        uniqueCustomers.add(loan.customer_name);
      }
    });
  }
  
  return uniqueCustomers.size;
};

export const prepareChitPoolData = (
  hasChitPoolData: boolean,
  chitPools?: any[]
) => {
  if (!hasChitPoolData || !chitPools) return [];
  
  return chitPools.slice(0, 5).map(pool => ({
    name: pool.name?.slice(0, 15) + (pool.name?.length > 15 ? '...' : '') || 'Unnamed',
    amount: pool.amount
  }));
};

export const prepareLoanData = (
  hasLoanData: boolean,
  loans?: any[]
) => {
  if (!hasLoanData || !loans) return [];
  
  return loans.slice(0, 5).map(loan => ({
    name: loan.customer_name?.slice(0, 15) + (loan.customer_name?.length > 15 ? '...' : '') || 'Unnamed',
    amount: loan.amount,
    interest: loan.monthly_interest || (loan.amount * loan.interest_rate / 100 / 12)
  }));
};

export const preparePieChartData = (
  chitPoolsCount: number = 0,
  loansCount: number = 0
) => {
  return [
    { name: 'Chit Pools', value: chitPoolsCount },
    { name: 'Loans', value: loansCount }
  ];
};

export const preparePortfolioValueData = (
  totalChitPoolValue: number,
  totalLoanValue: number
) => {
  return [
    { name: 'Chit Pools', value: totalChitPoolValue },
    { name: 'Loans', value: totalLoanValue }
  ];
};

export const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR'
  }).format(amount);
};

export const generateInterestData = (hasLoanData: boolean, loanData: any[]) => {
  const currentDate = new Date();
  const data = [];
  
  if (hasLoanData) {
    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentDate);
      date.setMonth(currentDate.getMonth() - i);
      
      const totalMonthlyInterest = loanData.reduce((sum, loan) => sum + (loan.interest || 0), 0);
      
      data.push({
        month: date.toLocaleString('default', { month: 'short', year: 'numeric' }),
        interest: totalMonthlyInterest
      });
    }
  } else {
    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentDate);
      date.setMonth(currentDate.getMonth() - i);
      
      data.push({
        month: date.toLocaleString('default', { month: 'short', year: 'numeric' }),
        interest: 0
      });
    }
  }
  
  return data;
};
